// Sheets opened from the phone tab bar: More (the whole sidebar, grouped), Files (search + recent files), Inbox (the mailboxes).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bug, Construction, Download, Loader2, LogOut, Search, Settings, User, Clock, FolderOpen, Bell } from "lucide-react";
import { Input } from "@/components/ui/input";
import { BottomSheet, SheetRow, SectionTitle } from "./PhoneBits";
import { listAgeingCases } from "@/lib/api";
import { ageingBasePath, setCollectionsRegion, fmtMoney } from "@/lib/regionFmt";
import { useRegion } from "@/contexts/RegionContext";
import { recentFiles } from "@/lib/recentFiles";
import { APP_VERSION } from "@/lib/releaseNotes";

const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

// Same path → badge mapping as the desktop sidebar
export function countFor(path, n) {
  if (!n) return 0;
  if (path === "/approvals") return n.pendingApprovals;
  if (path === "/legal/documents" || path === "/aus/legal/documents") return n.pendingDocRequests;
  if (path === "/legal/mail-inbox") return n.legalMailInbox;
  if (path === "/legal/inbox" || path === "/aus/legal/inbox") return n.legalInbox;
  if (path === "/collections/inbox" || path === "/aus/collections/inbox") return n.accountsInbox;
  if (path === "/applications/inbox") return n.appsInbox;
  return 0;
}

export function MoreSheet({ open, onClose, categories, bottomNav, notifications, onReportBug, installable, onInstall, user, onLogout }) {
  const navigate = useNavigate();
  const go = (path) => { onClose(); navigate(path); };
  return (
    <BottomSheet open={open} onClose={onClose} title="More" testid="phone-more-sheet">
      <div className="space-y-4 -mx-1">
        {categories.map((cat) => (
          <div key={cat.id} data-testid={`phone-more-group-${cat.id}`}>
            <SectionTitle>{cat.label}</SectionTitle>
            <div className="mt-1">
              {cat.items.map((it) => (
                <SheetRow key={it.path} icon={it.underConstruction ? Construction : it.icon} label={it.label}
                  sub={it.underConstruction ? "Coming soon" : undefined} disabled={!!it.underConstruction}
                  badge={countFor(it.path, notifications)} onClick={() => go(it.path)} testid={`phone-more-${slug(it.label)}`} />
              ))}
            </div>
          </div>
        ))}
        <div>
          <SectionTitle>Me</SectionTitle>
          <div className="mt-1">
            <SheetRow icon={User} label="My Profile" sub={user?.email} onClick={() => go("/profile")} testid="phone-more-profile" />
            <SheetRow icon={Bell} label="Notifications" sub="Push alerts on this device" onClick={() => go("/notifications")} testid="phone-more-notifications" />
            {bottomNav.map((it) => (
              <SheetRow key={it.path} icon={it.icon || Settings} label={it.label} onClick={() => go(it.path)} testid={`phone-more-${slug(it.label)}`} />
            ))}
            {installable && <SheetRow icon={Download} label="Install app" sub="Add SimplyFunded to your home screen" onClick={() => { onClose(); onInstall(); }} testid="phone-more-install" />}
            <SheetRow icon={Bug} label="Report a bug" onClick={() => { onClose(); onReportBug(); }} testid="phone-more-bug" tone="text-red-700" />
            <SheetRow icon={LogOut} label="Sign out" onClick={() => { onClose(); onLogout(); }} testid="phone-more-signout" tone="text-red-700" />
          </div>
        </div>
        <div className="text-center text-[11px] font-mono text-slate-400 pb-1" data-testid="phone-more-version">SimplyFunded Portal V{APP_VERSION}</div>
      </div>
    </BottomSheet>
  );
}

export function FilesSheet({ open, onClose, searchFirst = false }) {
  const navigate = useNavigate();
  const { currentRegion } = useRegion();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const recents = recentFiles();

  useEffect(() => { if (!open) { setQ(""); setResults(null); } }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (term.length < 2) { setResults(null); return undefined; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        setCollectionsRegion(currentRegion);
        const r = await listAgeingCases({ search: term, sort_by: "days_past_due", sort_dir: "desc" });
        setResults({ cases: (r.cases || []).slice(0, 25), off: (r.off_list_matches || []).slice(0, 10) });
      } catch { setResults({ cases: [], off: [] }); } finally { setBusy(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, open, currentRegion]);

  const openFile = (c) => { onClose(); navigate(`${ageingBasePath()}/${c.case_id}`); };

  return (
    <BottomSheet open={open} onClose={onClose} title="Files" testid="phone-files-sheet">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer name or SF number…" className="pl-9 h-11 text-base" autoFocus={searchFirst} data-testid="phone-files-search" />
        {busy && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
      </div>
      {results ? (
        <div data-testid="phone-files-results">
          {results.cases.length === 0 && results.off.length === 0 && !busy && <div className="text-sm text-slate-500 py-6 text-center">No file matches “{q.trim()}”.</div>}
          {results.cases.map((c) => (
            <SheetRow key={c.case_id} icon={FolderOpen} label={c.customer_name || "—"}
              sub={`${c.loan_application_id || ""} · ${c.days_past_due ?? "—"} DPD · ${fmtMoney(c.arrears_amount || 0, 0)} arrears`}
              onClick={() => openFile(c)} testid={`phone-files-result-${c.case_id}`} />
          ))}
          {results.off.length > 0 && (
            <>
              <SectionTitle>Not on today's worklist</SectionTitle>
              {results.off.map((c) => (
                <SheetRow key={c.case_id} icon={FolderOpen} label={c.customer_name || "—"} sub={`${c.loan_application_id || ""} · ${c.held_in_legal ? "held in Legal" : "off the arrears report"}`}
                  onClick={() => openFile(c)} testid={`phone-files-offlist-${c.case_id}`} />
              ))}
            </>
          )}
        </div>
      ) : (
        <div data-testid="phone-files-recent">
          <SectionTitle>Recent files</SectionTitle>
          {recents.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Files you open will appear here. Search above to find one.</div>}
          {recents.map((f) => (
            <SheetRow key={f.case_id} icon={Clock} label={f.name || f.case_id} sub={f.sf} onClick={() => { onClose(); navigate(f.path); }} testid={`phone-files-recent-${f.case_id}`} />
          ))}
        </div>
      )}
    </BottomSheet>
  );
}

const INBOX_PATHS = new Set(["/collections/inbox", "/aus/collections/inbox", "/legal/inbox", "/aus/legal/inbox", "/applications/inbox", "/legal/mail-inbox"]);

export function InboxSheet({ open, onClose, categories, notifications }) {
  const navigate = useNavigate();
  const items = categories.flatMap((c) => c.items).filter((it) => INBOX_PATHS.has(it.path) && !it.underConstruction);
  return (
    <BottomSheet open={open} onClose={onClose} title="Inboxes" testid="phone-inbox-sheet">
      <div className="-mx-1">
        {items.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No inboxes on your account.</div>}
        {items.map((it) => (
          <SheetRow key={it.path} icon={it.icon} label={it.label} badge={countFor(it.path, notifications)} onClick={() => { onClose(); navigate(it.path); }} testid={`phone-inbox-${slug(it.label)}`} />
        ))}
      </div>
      <p className="text-[11px] text-slate-500 px-1">The inboxes open in their desktop layout for now (scroll sideways where needed) — a phone layout is coming in the next release.</p>
    </BottomSheet>
  );
}
